import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Container, Col, Row, Button } from 'reactstrap';
import moment from 'moment';
import CardPanel from '../../../shared/components/widgets/CardPanel';
import Notify from '../../../shared/components/widgets/Notify';
import MultiPlatformBudgetToolForm, { ACTIVE_DAY_FIELDS, getSelectedCombos } from './form/MultiPlatformBudgetToolForm';
import {
  getBudgetGroups, saveBudgetGroup, searchGoogleLanguageConstants, searchGoogleGeoConstants,
} from '../shared/budgetApi';
import { serializeToBudgetJson } from '../shared/budgetJsonUtils';
import {
  buildUnifiedSubDetails,
  computeUnifiedDailyBudget,
  getUnifiedAmazonDefaults,
} from '../shared/constants';
import BudgetNotesPanel from './BudgetNotesPanel';
import useBudgetNotes from '../shared/useBudgetNotes';
import {
  MPBT_BUDGET_COMBOS,
  MPBT_LANGUAGE_OPTIONS,
  getComboBudgetTypeOption,
  resolveComboForRow,
  deriveRowBudgetTypeValue,
  stripComboSuffix,
  resolveGoogleLanguageConstants,
  resolveGoogleCountryConstants,
} from '../shared/mpbtConstants';

const MPBT_PLATFORMS_TO_FETCH = ['amazon', 'google', 'meta'];

const combosIncludePlatform = (combos, platform) => combos.some(combo => combo.platform === platform);

/**
 * Multi-Platform Budget Tool (MPBT) page. Sits alongside the standard per-platform Budget
 * pages (selected via the "Multi" retailer option). Lets a user create budget
 * groups across a fixed set of Platform + Budget Type combos (see
 * MPBT_BUDGET_COMBOS in mpbtConstants.js) for an account in one action, and shows a
 * list of existing MPBT-created groups (matched by shared name prefix across combos).
 */
export default function MultiPlatformBudgetToolPage({ onBackToListing }) {
  const sellerSelect = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [mpbtGroups, setMpbtGroups] = useState([]);

  // Notes follow the ad platforms of the checked combos (3 Google combos = just Google).
  // With nothing checked, only the 'all' notes are shown.
  const createFormValues = useSelector(state => state.form?.mpbtBudgetGroupCreate?.values);
  const selectedPlatforms = [...new Set(getSelectedCombos(createFormValues).map(combo => combo.platform))];
  const {
    notes: budgetNotes, loading: budgetNotesLoading, addNote, updateNote,
  } = useBudgetNotes({
    accountId: sellerSelect.value,
    retailers: selectedPlatforms,
    jwtToken: user.jwtToken,
  });

  const fetchAllPlatformGroups = useCallback(async () => {
    setLoadingList(true);
    const results = await Promise.allSettled(
      MPBT_PLATFORMS_TO_FETCH.map(platform => getBudgetGroups({
        accountId: sellerSelect.value,
        retailer: platform,
        platform,
        jwtToken: user.jwtToken,
      })),
    );

    // Group by name prefix only - each combo is its own row within the group.
    const groupsByPrefix = {};

    results.forEach((result, index) => {
      const platform = MPBT_PLATFORMS_TO_FETCH[index];
      if (result.status !== 'fulfilled' || !result.value?.budgetGroupData) {
        return;
      }

      result.value.budgetGroupData.forEach(row => {
        const prefix = stripComboSuffix(row.budget_group_name);
        const rowBudgetTypeValue = deriveRowBudgetTypeValue(platform, row);
        const combo = resolveComboForRow(platform, rowBudgetTypeValue);
        if (!combo) return; // Not an MPBT-recognized combo, skip

        if (!groupsByPrefix[prefix]) {
          groupsByPrefix[prefix] = { prefix, combos: {} };
        }
        groupsByPrefix[prefix].combos[combo.key] = row;
      });
    });

    // Only show prefixes that have more than one combo configured (true multi-combo groups)
    const multiComboGroups = Object.values(groupsByPrefix)
      .filter(group => Object.keys(group.combos).length > 1)
      .sort((a, b) => a.prefix.localeCompare(b.prefix));

    setMpbtGroups(multiComboGroups);
    setLoadingList(false);
  }, [sellerSelect.value, user.jwtToken]);

  useEffect(() => {
    if (sellerSelect.value) {
      fetchAllPlatformGroups();
    }
  }, [sellerSelect.value, fetchAllPlatformGroups]);

  return (
    <Container className="dashboard">
      <Col md={12}>
        <Row style={{ maxWidth: '1200px' }}>
          <CardPanel showButtons={false} title="Multi-Platform Budget Groups" subhead="Create and manage budget groups across Amazon, Google, and Meta at once">
            <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
              <Button color="secondary" style={{ margin: 0, whiteSpace: 'nowrap' }} onClick={onBackToListing}>
                &larr; Back to Budget Listing
              </Button>
              <Button color="primary" style={{ margin: 0, whiteSpace: 'nowrap' }} onClick={() => setShowCreateForm(prev => !prev)}>
                {showCreateForm ? 'Close' : 'Create Multi-Platform Budget Group'}
              </Button>
            </div>

            {showCreateForm && (
              <MpcCreateFormWrapper
                sellerSelect={sellerSelect}
                user={user}
                disableButton={disableButton}
                setDisableButton={setDisableButton}
                onCancel={() => setShowCreateForm(false)}
                onSaved={() => {
                  setShowCreateForm(false);
                  fetchAllPlatformGroups();
                }}
              />
            )}

            <MpbtGroupsList groups={mpbtGroups} loading={loadingList} />
          </CardPanel>
        </Row>
        <Row style={{ maxWidth: '1200px' }}>
          <BudgetNotesPanel
            notes={budgetNotes}
            loading={budgetNotesLoading}
            addNote={addNote}
            updateNote={updateNote}
            defaultRetailer={selectedPlatforms.length === 1 ? selectedPlatforms[0] : 'all'}
          />
        </Row>
      </Col>
    </Container>
  );
}

MultiPlatformBudgetToolPage.propTypes = {
  onBackToListing: PropTypes.func,
};

MultiPlatformBudgetToolPage.defaultProps = {
  onBackToListing: undefined,
};

/**
 * Wraps MultiPlatformBudgetToolForm with the redux-form values selector (needed so the
 * form can reactively show/hide combo daily-budget inputs and per-combo settings
 * sections as the user checks boxes) and owns the actual multi-combo save logic.
 */
function MpcCreateFormWrapper({
  sellerSelect, user, disableButton, setDisableButton, onCancel, onSaved,
}) {
  const formValues = useSelector(state => state.form?.mpbtBudgetGroupCreate?.values);

  // Memoized so reduxForm's enableReinitialize doesn't see a brand-new object
  // reference on every render, which would otherwise trigger an infinite
  // reinitialize -> re-render -> reinitialize loop.
  const initialFormValues = useMemo(() => ({
    setupFlow: { label: 'Advanced Legacy', value: 'advanced' },
    agencyInternal: user.access === 'admin' ? { label: 'Limitless View', value: true } : { label: 'Agency View', value: false },
    state: { label: 'Draft', value: 'draft' },
    spendSpeed: { label: 'Standard', value: 'speed_level_1' },
    spendTactic: { label: 'Purchase Full Funnel SA', value: 'purchase_full_funnel_sa' },
    spendPriority: { label: 'Brand and Non-Brand', value: '0' },
    costTargetType: { label: 'Brand/NonBrand', value: 'bnb' },
    costTarget: 4,
    costTargetBrand: 4,
    costTargetNonBrand: 4,
    targetCountries: 'US',
    startDate: moment(),
    languages: ['en_US'],
    googleSalesCountry: { label: 'United States', value: 'US' },
    meta_facebook_instagram_gender: { label: 'All Genders', value: 0 },
    meta_facebook_instagram_ageMin: 18,
    meta_facebook_instagram_ageMax: 65,
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: true,
    sunday: true,
    triggerDefaultJobs: false,
  }), [user.access]);

  const handleSubmit = async (formData) => {
    setDisableButton(true);

    const selectedCombos = getSelectedCombos(formData);
    const activeDaysCsv = ACTIVE_DAY_FIELDS.map(day => (formData[day.name] ? '1' : '0')).join(',');
    const normalizedUrl = formData.landingPageUrl || '';
    const normalizedBrandName = formData.brandName || '';
    const normalizedLanguages = formData.languages || [];

    const googleLanguageConstants = await resolveGoogleLanguageConstants(normalizedLanguages, searchGoogleLanguageConstants, user.jwtToken);
    const metaLanguageValues = normalizedLanguages
      .map(value => MPBT_LANGUAGE_OPTIONS.find(opt => opt.value === value)?.metaValue)
      .filter(Boolean);

    // Target Countries is one normalized input holding ISO codes (e.g. "US,CA").
    // Meta stores the codes directly; Google needs geoTargetConstants resource names.
    const targetCountryCodes = (formData.targetCountries || '')
      .split(',')
      .map(code => code.trim())
      .filter(Boolean);
    const googleCountryConstants = combosIncludePlatform(selectedCombos, 'google')
      ? await resolveGoogleCountryConstants(targetCountryCodes, searchGoogleGeoConstants, user.jwtToken)
      : [];

    const results = await Promise.allSettled(selectedCombos.map(async (combo) => {
      const comboBudgetTypeOption = getComboBudgetTypeOption(combo);
      const p = (suffix) => formData[`${combo.key}_${suffix}`];
      const isUnifiedAmazon = combo.platform === 'amazon';
      // Amazon is Unified-only: Daily Budget is the sum of the checked sub-type amounts and
      // the hidden fields are forced values (same as the single Unified BGD form).
      const unifiedDefaults = isUnifiedAmazon ? getUnifiedAmazonDefaults(sellerSelect.type) : {};
      const dailyBudget = isUnifiedAmazon
        ? computeUnifiedDailyBudget(formData)
        : parseFloat(formData[`combo_${combo.key}_dailyBudget`]) || 0;

      const budgetJsonSourceValues = {
        googleBrandName: normalizedBrandName,
        googleSalesCountry: formData.googleSalesCountry || 'US',
        googleLanguages: googleLanguageConstants.length ? googleLanguageConstants.join(', ') : 'languageConstants/1000',
        geoCountries: combo.platform === 'google' ? googleCountryConstants.join(',') : targetCountryCodes.join(','),
        geoStates: formData.googleGeoStates || '',
        geoCities: formData.googleGeoCities || '',
        geoPostalCodes: formData.googleGeoPostalCodes || '',
        metaLanguages: metaLanguageValues,
        metaGender: p('gender'),
        metaAgeMin: p('ageMin'),
        metaAgeMax: p('ageMax'),
        productSetId: p('productSetId') || '',
        // Amazon Unified keywords (negative keywords are never captured for Unified)
        ...(isUnifiedAmazon && {
          negativeKeywords: '',
          negativePhrase: false,
          negativeExact: false,
          customKeywords: formData.customKeywords,
          customBroad: formData.customBroad,
          customPhrase: formData.customPhrase,
          customExact: formData.customExact,
        }),
        customAudienceIds: p('customAudienceIds') || [],
        excludedCustomAudienceIds: p('excludedCustomAudienceIds') || [],
        metaIncome: p('income') || [],
        metaFamilyStatuses: p('familyStatuses') || [],
      };

      // Mirrors GroupEditModal.jsx's budgetJson construction exactly: Amazon needs
      // the allocation.percentages/allocation.original shape, while every other
      // platform just stores daily/monthly directly at the root.
      const budgetJson = {
        ...(combo.platform === 'amazon' ? {
          allocation: {
            percentages: {
              sp_sd_percent: 100,
              sb_percent: 0,
              st_percent: 0,
              dsp_percent: 0,
            },
            original: {
              daily: { budget: dailyBudget, reallocation: 0 },
              monthly: {},
            },
          },
        } : {
          daily: { budget: dailyBudget },
          monthly: {},
        }),
        ...serializeToBudgetJson(budgetJsonSourceValues, combo.platform, combo.budgetTypeValue),
      };

      // Google Search's ASINs and Standard Shopping's Product IDs map to the same
      // masterItemOnList/masterItemOffList columns the single-platform form uses,
      // depending on which combo this is; other combos use the normalized URL.
      let masterItemOnList = normalizedUrl;
      let masterItemOffList = '';
      if (combo.platform === 'amazon') {
        masterItemOnList = p('masterItemOnList') || '';
        masterItemOffList = p('masterItemOffList') || '';
      } else if (combo.key === 'google_search') {
        masterItemOffList = p('asins') || '';
      } else if (combo.key === 'google_shop') {
        masterItemOnList = p('productItemIds') || normalizedUrl;
      }

      // Non-Amazon platforms always use a single budgetGroupRecords entry at 100%
      // allocation (mirrors GroupEditModal.jsx's non-Amazon branch). Amazon is Unified-only:
      // SP at 100% (not the legacy combined 'SP,SD'); SD, SB and ST stay at 0.
      const budgetGroupRecords = combo.platform === 'amazon'
        ? [
          { budget_group_daily_id: 0, ad_type: 'SP', allocation_percent: 100 },
          { budget_group_daily_id: 0, ad_type: 'SD', allocation_percent: 0 },
          { budget_group_daily_id: 0, ad_type: 'SB', allocation_percent: 0 },
          { budget_group_daily_id: 0, ad_type: 'ST', allocation_percent: 0 },
        ]
        : [
          { budget_group_daily_id: 0, ad_type: comboBudgetTypeOption?.adType || combo.budgetTypeValue.toUpperCase(), allocation_percent: 100 },
        ];
      const activeRecords = budgetGroupRecords.filter(r => r.allocation_percent > 0);
      const adTypeCsv = activeRecords.map(r => r.ad_type).join(',');
      const budgetGroupDailyIdCsv = activeRecords.map(r => r.budget_group_daily_id).join(',');

      const submitBudgetGroupDaily = {
        accountId: sellerSelect.value,
        accountTypeId: sellerSelect.type,
        budgetGroupRecords,
        budgetGroupDailyIdCsv,
        budgetGroupName: `${formData.namePrefix} - ${combo.nameSuffix}`,
        setupFlow: isUnifiedAmazon ? 'unified' : formData.setupFlow?.value,
        retailer: combo.platform,
        agencyInternal: formData.agencyInternal?.value,
        activeDaysCsv,
        adTypeCsv,
        portfolioIdsCsv: '',
        includes: isUnifiedAmazon ? unifiedDefaults.includes.value : '',
        costTargetType: isUnifiedAmazon ? unifiedDefaults.costTargetType.value : (formData.costTargetType?.value || 'blended'),
        costTargetMetric: 'roas',
        costTargetBrand: (isUnifiedAmazon || formData.costTargetType?.value === 'bnb') ? formData.costTargetBrand : formData.costTarget,
        costTargetNonBrand: (isUnifiedAmazon || formData.costTargetType?.value === 'bnb') ? formData.costTargetNonBrand : formData.costTarget,
        spendSpeed: isUnifiedAmazon ? unifiedDefaults.spendSpeed.value : (formData.spendSpeed?.value || 'speed_level_1'),
        spendTactic: (() => {
          if (isUnifiedAmazon) return unifiedDefaults.spendTactic.value;
          return (combo.platform === 'google' || combo.platform === 'meta') ? 'purchase_full_funnel_sa' : (formData.spendTactic?.value || 'max_perf');
        })(),
        limitlessBudget: 0,
        spendPriority: isUnifiedAmazon ? unifiedDefaults.spendPriority.value : (formData.spendPriority?.value || '0'),
        itemType: isUnifiedAmazon ? unifiedDefaults.itemType.value : (comboBudgetTypeOption?.itemType || undefined),
        itemSelection: 'ALL',
        destination: 'ON',
        masterItemOrigList: masterItemOnList,
        masterItemOnList,
        masterItemOffList,
        startDate: formData.startDate,
        endDate: formData.endDate,
        budgetDriver: 'daily',
        budgetJson,
        ...(isUnifiedAmazon && { budget_group_daily_sub_details: buildUnifiedSubDetails(formData) }),
        state: formData.state?.value,
        budgetType: combo.budgetTypeValue,
        brandName: normalizedBrandName,
        triggerDefaultJobs: !!formData.triggerDefaultJobs,
      };

      return saveBudgetGroup({ data: submitBudgetGroupDaily, jwtToken: user.jwtToken })
        .then(() => ({ combo, success: true }))
        .catch((error) => ({ combo, success: false, error }));
    }));

    setDisableButton(false);

    const successCombos = [];
    const failedCombos = [];
    results.forEach(result => {
      if (result.status === 'fulfilled' && result.value?.success) {
        successCombos.push(result.value.combo.label);
      } else {
        failedCombos.push(result.value?.combo?.label || 'unknown');
      }
    });

    if (successCombos.length) {
      Notify.show('success', 'Success', `Budget groups created for: ${successCombos.join(', ')}`);
    }
    if (failedCombos.length) {
      Notify.show('danger', 'Error', `Failed to create budget groups for: ${failedCombos.join(', ')}`);
    }
    if (!failedCombos.length) {
      onSaved();
    }
  };

  return (
    <MultiPlatformBudgetToolForm
      onSubmit={handleSubmit}
      onCancel={onCancel}
      disableButton={disableButton}
      formValues={formValues}
      initialValues={initialFormValues}
      user={user}
    />
  );
}

MpcCreateFormWrapper.propTypes = {
  sellerSelect: PropTypes.shape().isRequired,
  user: PropTypes.shape().isRequired,
  disableButton: PropTypes.bool.isRequired,
  setDisableButton: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

/**
 * Read-only list of existing multi-combo budget groups, grouped by shared name
 * prefix. Only prefixes with more than one combo configured are shown.
 */
function MpbtGroupsList({ groups, loading }) {
  if (loading) {
    return <p>Loading multi-platform budget groups...</p>;
  }

  if (!groups.length) {
    return <p>No multi-platform budget groups found for this account (a prefix must have more than one Platform - Budget Type combo configured to appear here).</p>;
  }

  return (
    <div style={{ overflowX: 'auto', width: '100%' }}>
      <table className="table" style={{ minWidth: '600px' }}>
        <thead>
          <tr>
            <th style={{ whiteSpace: 'nowrap' }}>Name Prefix</th>
            {MPBT_BUDGET_COMBOS.map(combo => (
              <th key={combo.key} style={{ whiteSpace: 'nowrap' }}>{combo.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {groups.map(group => (
            <tr key={group.prefix}>
              <td>{group.prefix}</td>
              {MPBT_BUDGET_COMBOS.map(combo => (
                <td key={combo.key}>{group.combos[combo.key] ? '✓' : '—'}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

MpbtGroupsList.propTypes = {
  groups: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  loading: PropTypes.bool.isRequired,
};
